/**
 * A folha de SAÍDAS de um mês de uma congregação, só leitura.
 *
 * **Fase 4 do espelho**, aprovada em 12/10/2026. A marcação é a do notebook, copiada de
 * `../adsa-financas/src/renderer/pages/Saidas.tsx`, com tudo o que digita, anexa, estorna e tira
 * fora: o que sobra é o papel.
 *
 * **Tela própria, e não uma terceira folha na de Congregação.** Aquela tela É o documento do
 * Relatório de Movimentação, que o servidor fotografa; acrescentar uma folha lá mudaria um PDF
 * que o pastor já usa.
 *
 * As três coisas que o pastor precisa reconhecer, e que por isso saem iguais às de lá:
 *
 * 1. o título `SAÍDAS – <MÊS>`, em maiúsculas;
 * 2. as cinco colunas, nesta ordem: `DATA`, `CATEGORIA`, `DESCRIÇÃO`, `VALOR`, `SITUAÇÃO`;
 * 3. o rodapé `TOTAL APROVADO NO MÊS`, com o que está aguardando ao lado, e embaixo a linha dos
 *    três bolsos, com a frase que explica que **só o caixa sai do saldo da congregação**.
 */
import { useEffect, useState, type JSX } from 'react'
import {
  ehSessaoTerminada, ErroDoServidor, servidor,
  type Eu, type FolhaDeSaidas, type LinhaDeSaida
} from '../api'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { competenciaPorExtenso, emReais, mesAnterior, mesSeguinte, NOME_DA_SITUACAO } from '../dinheiro'
import { ehParaImprimir, enderecoDaCongregacao, enderecoDasSaidasDaCongregacao, irPara } from '../rotas'

/** `2026-08-05` vira `05/08/2026`, como no notebook. */
function porExtenso(data: string): string {
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

export function SaidasDoMes({
  eu,
  id,
  competenciaDoEndereco,
  aoSair
}: {
  eu: Eu
  id: string
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const [dados, setDados] = useState<FolhaDeSaidas | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const paraImprimir = ehParaImprimir()

  useEffect(() => {
    let vale = true
    setErro(null)
    setDados(null)

    servidor
      .saidasDaCongregacao(id, competenciaDoEndereco)
      .then((recebido) => {
        if (vale) setDados(recebido)
      })
      .catch((falha: unknown) => {
        if (!vale) return
        if (ehSessaoTerminada(falha)) return aoSair()
        setErro(falha instanceof ErroDoServidor ? falha.message : 'Não foi possível carregar.')
      })

    return () => {
      vale = false
    }
  }, [id, competenciaDoEndereco, aoSair])

  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDasSaidasDaCongregacao(id, para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima
          eu={eu}
          aoSair={aoSair}
          voltarPara={{ endereco: enderecoDaCongregacao(id, mes), texto: 'Folhas' }}
        />
      )}

      <main
        className="folha folha-lancamento"
        data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}
      >
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            <header className="cabecalho-da-congregacao">
              <h1>{dados.congregacao.nome}</h1>
              <p className="explicacao">
                Saídas de {dados.competencia_por_extenso}
                {' · '}
                <span className={`situacao ${dados.situacao_do_mes}`}>
                  {NOME_DA_SITUACAO[dados.situacao_do_mes] ?? dados.situacao_do_mes}
                </span>
              </p>
            </header>

            {mes !== null && !paraImprimir && (
              <nav className="navegar-mes so-tela">
                <button aria-label="Mês anterior" onClick={() => andar(mesAnterior(mes))}>◀</button>
                <strong>{competenciaPorExtenso(mes)}</strong>
                <button aria-label="Mês seguinte" onClick={() => andar(mesSeguinte(mes))}>▶</button>
              </nav>
            )}

            <section className="folha-de-um-tipo">
              <h2>SAÍDAS – {dados.competencia_por_extenso.toUpperCase()}</h2>

              {dados.pendencias_para_fechar > 0 && (
              <p className="aviso so-tela">
                {dados.pendencias_para_fechar === 1
                  ? 'Uma saída está sem o comprovante que a categoria exige, e isso segura o fechamento do mês.'
                  : `${dados.pendencias_para_fechar} saídas estão sem o comprovante que a categoria exige, e isso segura o fechamento do mês.`}
              </p>
            )}

              {/* Some na tela grande, onde a folha cabe inteira, e some no papel. */}
              <p className="rola-para-o-lado so-tela">
                Arraste a folha para o lado para ver todas as colunas.
              </p>

              <table className="grade folha-de-saidas">
                <colgroup>
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '23%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="rotulo">DATA</th>
                    <th className="rotulo">CATEGORIA</th>
                    <th className="rotulo">DESCRIÇÃO</th>
                    <th className="valor">VALOR</th>
                    <th className="rotulo">SITUAÇÃO</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.linhas.length === 0 && (
                    <tr>
                      <td colSpan={5} className="lapis">
                        Nenhuma saída lançada em {dados.competencia_por_extenso}.
                      </td>
                    </tr>
                  )}

                  {dados.linhas.map((l) => <LinhaDaSaida key={l.id} linha={l} />)}

                  {dados.linhas.length > 0 && (
                    <tr className="rodape">
                      <td colSpan={3}>TOTAL APROVADO NO MÊS</td>
                      <td className="valor">{emReais(dados.total_aprovado)}</td>
                      <td>
                        {dados.total_pendente > 0 && `+ ${emReais(dados.total_pendente)} aguardando`}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {dados.linhas.length > 0 && (
                <p className="explicacao">
                  Do caixa: <strong>{emReais(dados.por_bolso.CAIXA)}</strong> · do cartão:{' '}
                  {emReais(dados.por_bolso.CARTAO)} · pela conta do Campo:{' '}
                  {emReais(dados.por_bolso.CONTA_CAMPO)}.{' '}
                  <strong>Só o caixa sai do saldo da congregação.</strong>
                </p>
              )}
            </section>
          </>
        )}
      </main>
    </>
  )
}

/**
 * Uma linha da folha.
 *
 * As etiquetas da descrição e da situação são as do notebook, e cada uma existe por um motivo:
 * o fundo e o bolso dizem de que dinheiro saiu, e a pendência do comprovante é o que segura o
 * fechamento do mês.
 */
function LinhaDaSaida({ linha }: { linha: LinhaDeSaida }): JSX.Element {
  return (
    <tr className={linha.e_estorno ? 'estorno' : undefined}>
      <td className="impresso data">{porExtenso(linha.data)}</td>
      <td className="impresso">{linha.categoria}</td>
      <td className="impresso">
        {linha.descricao}
        {linha.fundo !== null && <span className="etiqueta">{linha.fundo}</span>}
        {linha.bolso_curto !== null && <span className="etiqueta">{linha.bolso_curto}</span>}
        {linha.custo_geral_do_campo && <span className="etiqueta">Campo</span>}
      </td>
      <td className="valor manuscrito">{emReais(linha.valor)}</td>
      <td>
        {linha.situacao}
        {linha.aprovado_por !== null && !linha.status.startsWith('PENDENTE') && (
          <span className="explicacao">: {linha.aprovado_por}</span>
        )}
        {/* Vai ao papel também, como na folha do notebook (0.3.21, caso A37). */}
        {linha.paga_por_outro !== null && (
          <span className="explicacao paga-por-outro">; {linha.paga_por_outro}</span>
        )}
        {linha.do_extrato && (
          <span className="etiqueta so-tela" title="O lançamento do banco vale como comprovante">
            extrato do banco
          </span>
        )}
        {linha.falta_comprovante && <span className="etiqueta alerta">falta o comprovante</span>}
      </td>
    </tr>
  )
}
